import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Employees } from 'src/app/afas-classes/employees.class';
import { Role } from 'src/app/classes/auth/role.class';
import { Entity } from 'src/app/classes/entity.class';
import { EntityUnavailable } from 'src/app/classes/entityunavailable.class';
import { User } from 'src/app/classes/user.class';
import { AfasService } from 'src/app/services/afas.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { EntityType, EntityTypeCode } from 'src/app/services/entities/entity-type.class';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { UserService } from 'src/app/services/user/user.service';
import { Utils } from 'src/app/utils.class';
import { daterangeAvailableValidator, DateRangeValidatorType } from 'src/app/validators/daterange-available.validator';
import { CodaltComponent } from '../../../codalt.component';
import { DateFullMonthAdapter } from '../../../date-adapters/date-full-month-adapter';
import { LocalStorage } from '../../../storage.class';

@Component({
    selector: 'app-entity-detail',
    templateUrl: './entity-detail.component.html',
    styleUrls: ['./entity-detail.component.scss'],
    providers: [{ provide: DateAdapter, useClass: DateFullMonthAdapter }],
    standalone: false,
})
export class EntityDetailComponent extends CodaltComponent implements OnInit {
    unavailablePlanningForm: Map<EntityUnavailable, FormGroup>;
    form: FormGroup;
    saving = false;
    title = '';
    submitted = false;
    truckDrivers: User[];
    entityTeams: Entity[];
    entityTrucks: Entity[];
    entityLowloaders: Entity[];
    lowloaderPlaceholder: string;
    entitytypeList: EntityType[];
    roles: Role[];
    employees: User[];
    executors: User[];
    asphaltExecutors: User[];
    viewState = {
        driver: false,
        preferredTeam: false,
        truck: false,
        lowloader: false,
        employees: false,
        asphaltPerformer: false,
        performer: false,
        vkm: false,
    };
    afasMaterieel: Employees[];

    EntityTypeCode = EntityTypeCode;
    LocalStorage = LocalStorage;

    constructor(
        @Inject(MAT_DIALOG_DATA) public entity: Entity,
        private snackBar: MatSnackBar,
        private confirmDialog: ConfirmDialogService,
        public dialogRef: MatDialogRef<EntityDetailComponent>,
        private userService: UserService,
        private planningService: PlanningService,
        private entitiesService: EntitiesService,
        public authorisationService: AuthorisationService,
        private afasService: AfasService
    ) {
        super();
    }

    ngOnInit() {
        this.unavailablePlanningForm = new Map<EntityUnavailable, FormGroup>([]);
        this.subscriptions.add(
            this.userService.getList(false, false).subscribe(truckDrivers => {
                this.truckDrivers = truckDrivers;
            })
        );

        this.subscriptions.add(
            this.entitiesService.getTypes().subscribe(types => {
                this.entitytypeList = types.data;
            })
        );

        this.subscriptions.add(
            this.authorisationService.getRolesMap().subscribe(rolesMap => {
                this.roles = Array.from(rolesMap.values()).filter(
                    role => !role.restricted || AuthorisationService.hasRole(this.environment.roles.Codalt)
                );
            })
        );

        this.subscriptions.add(
            this.entitiesService.getByType(EntityTypeCode.AsfaltTeam).subscribe(entities => {
                this.entityTeams = entities.filter(
                    e => e.enddate === null || new Date(e.enddate).getTime() >= new Date().getTime()
                );
            })
        );
        this.subscriptions.add(
            this.entitiesService.getByType(EntityTypeCode.Truck).subscribe(entities => {
                this.entityTrucks = entities.filter(
                    e => e.enddate === null || new Date(e.enddate).getTime() >= new Date().getTime()
                );
            })
        );
        if (this.entity.id) {
            this.entitiesService.get(this.entity.id).then(entity => {
                this.createForm(entity);
                this.entity = entity;
                entity.entity_unavailable.forEach(unavailable => {
                    this.unavailablePlanningForm.set(unavailable, this.createUnavailableForm(unavailable));
                });
            });
        } else {
            this.createForm(this.entity);
            if (this.entity.id && this.entity.entity_unavailable) {
                this.entity.entity_unavailable.forEach(unavailable => {
                    this.unavailablePlanningForm.set(unavailable, this.createUnavailableForm(unavailable));
                });
            }
        }
        this.subscriptions.add(
            this.entitiesService.getTypesMap().subscribe(data => {
                this.title = data.get(this.entity.entitytypes[0]?.key)?.name;
            })
        );

        if (AuthorisationService.hasFunctionality(Functionalities.EntitiesManagement)) {
            this.subscriptions.add(
                this.afasService.getMaterieel().subscribe(materieel => {
                    this.afasMaterieel = materieel.data;
                })
            );
        } else {
            this.afasMaterieel = [];
        }
    }

    createForm(entity: Entity) {
        this.viewState.preferredTeam = Utils.entitytypeProperty(entity.entitytypes, 'has_preferred_team');
        this.viewState.driver = Utils.entitytypeProperty(entity.entitytypes, 'has_driver');
        this.viewState.truck = Utils.entitytypeProperty(entity.entitytypes, 'has_truck');
        this.viewState.lowloader = Utils.entitytypeProperty(entity.entitytypes, 'has_lowloader');
        this.viewState.employees = Utils.entitytypeProperty(entity.entitytypes, 'has_employees');
        this.viewState.asphaltPerformer = Utils.entitytypeProperty(entity.entitytypes, 'has_default_asphalt_performer');
        this.viewState.performer = Utils.entitytypeProperty(entity.entitytypes, 'has_default_performer');
        this.viewState.vkm = !!entity.entitytypes
            .map(et => et.key)
            .find(e => ([EntityTypeCode.VkmMateriaal, EntityTypeCode.VkmPreparation] as any).includes(e));

        if (this.viewState.lowloader) {
            this.lowloaderPlaceholder = this.getLowloaderPlaceholder(entity.entitytypes);
            this.subscriptions.add(
                this.entitiesService.getByType(this.getLowloaderDataFilter(entity.entitytypes)).subscribe(entities => {
                    this.entityLowloaders = entities.filter(
                        e => e.enddate === null || new Date(e.enddate).getTime() >= new Date().getTime()
                    );
                })
            );
        }

        this.form = new FormGroup({
            name: new FormControl(entity.name, [Validators.maxLength(60), Validators.required]),
            entitytypes: new FormControl(entity.entitytypes?.map(e => e.key)),
            roles: new FormControl(entity.roles?.map(role => role.key) ?? []),
            default_employees: new FormControl(entity.default_employees),
            default_performer_id: new FormControl(entity.default_performer_id),
            default_asphalt_performer_id: new FormControl(entity.default_asphalt_performer_id),
            driver_user_id: new FormControl(entity.driver_user_id),
            team_entity_id: new FormControl(entity.team_entity_id),
            afas_employee_id: new FormControl(entity.afas_employee_id),
            truck_entity_id: new FormControl(entity.truck_entity_id),
            lowloader_entity_id: new FormControl(entity.lowloader_entity_id),
            begindate: new FormControl(
                entity.begindate ? entity.begindate : new Date(),
                [Validators.required],
                [daterangeAvailableValidator(this.planningService, entity.id, DateRangeValidatorType.beginAsEnd, true)]
            ),
            enddate: new FormControl(
                entity.enddate,
                [],
                [daterangeAvailableValidator(this.planningService, entity.id, DateRangeValidatorType.endAsBegin, true)]
            ),
            exclude_driving_hours: new FormControl(entity.exclude_driving_hours),
            default_transport_none: new FormControl(entity.default_transport_none),
            default_transport_home: new FormControl(entity.default_transport_home),
            price: new FormControl(entity.price),
            price_unit: new FormControl(entity.price_unit),
            unit: new FormControl(entity.unit),
            settle_group: new FormControl(entity.settle_group),
            price_plotten: new FormControl(entity.price_plotten),
            coupling_clamps: new FormControl(entity.coupling_clamps),
            ground_pot: new FormControl(entity.ground_pot),
        });

        if (this.viewState.employees) {
            this.subscriptions.add(
                this.userService.getList(false, false).subscribe(users => {
                    this.employees = users;
                })
            );
        }

        if (this.viewState.performer) {
            this.subscriptions.add(
                this.userService.getByType(this.environment.roles.Executor).subscribe(users => (this.executors = users))
            );
        }

        if (this.viewState.asphaltPerformer) {
            this.subscriptions.add(
                this.userService
                    .getByType(this.environment.roles.Asphaltexecutor)
                    .subscribe(users => (this.asphaltExecutors = users))
            );
        }

        if (entity.afas_employee_id !== null) {
            this.form.get('name')?.disable();
        }
    }

    private getLowloaderDataFilter(entitytypes: EntityType[]): string | string[] {
        const entitytypeWithDataFilter = entitytypes
            .map(et => this.entitytypes[et.key])
            .find(et => et?.fields_phe?.lowloader_entity_id?.dataFilter);

        if (entitytypeWithDataFilter) {
            return entitytypeWithDataFilter.fields_phe.lowloader_entity_id.dataFilter;
        }

        console.warn(
            'Geen fields_phe.lowloader_entity_id.dataFilter geconfigureerd voor dit entitytype, val terug op EntityTypeCode.LowLoader'
        );
        return EntityTypeCode.LowLoader;
    }

    private getLowloaderPlaceholder(entitytypes: EntityType[]): string {
        const names = Array.from(
            new Set(
                entitytypes
                    .map(et => this.entitytypes[et.key]?.fields_phe?.lowloader_entity_id?.name)
                    .filter((name): name is string => !!name)
            )
        );

        return names.length ? names.join(' / ') : 'Dieplader';
    }

    createUnavailableForm(unavailable: EntityUnavailable) {
        return new FormGroup(
            {
                description: new FormControl(unavailable.description, [Validators.maxLength(300), Validators.required]),
                begindate: new FormControl(unavailable.begindate, [Validators.required]),
                enddate: new FormControl(unavailable.enddate, [Validators.required]),
            },
            [],
            [daterangeAvailableValidator(this.planningService, this.entity.id, DateRangeValidatorType.normal, true)]
        );
    }

    addUnavailable() {
        if (!this.entity.entity_unavailable) {
            this.entity.entity_unavailable = [];
        }
        const unavailable = new EntityUnavailable();
        this.entity.entity_unavailable.push(unavailable);
        this.unavailablePlanningForm.set(unavailable, this.createUnavailableForm(unavailable));
    }

    close() {
        this.dialogRef.close();
    }

    allFormsValid(): boolean {
        let allValid = true;

        this.unavailablePlanningForm.forEach((formGroup: FormGroup) => {
            if (!formGroup.valid) {
                for (const name of Object.keys(formGroup.controls)) {
                    formGroup.controls[name].markAsTouched();
                }
            }

            if (allValid) {
                allValid = formGroup.valid;
            }
        });

        return allValid;
    }

    deleteUnavailable(event: MouseEvent, unavailable: EntityUnavailable) {
        event.preventDefault();
        event.stopPropagation();

        const index = this.entity.entity_unavailable.indexOf(unavailable);

        this.entity.entity_unavailable.splice(index, 1);

        this.unavailablePlanningForm.delete(unavailable);
    }

    save() {
        this.submitted = true;
        this.saving = true;

        if (this.form.valid && this.allFormsValid()) {
            Object.assign(this.entity, this.form.value);

            this.unavailablePlanningForm.forEach((formGroup: FormGroup, unavailable: EntityUnavailable) => {
                Object.assign(unavailable, formGroup.value);

                this.setTime(unavailable);
            });

            this.setTime(this.entity);

            this.entitiesService.save(this.entity).then(
                entity => {
                    this.saving = false;
                    this.snackBar.open(`${entity.name} is opgeslagen`, 'Oké', { duration: 2000 });
                    this.close();
                },
                () => {
                    this.saving = false;
                    this.confirmDialog.confirm({
                        title: 'Fout tijdens het opslaan',
                        text: 'Er is iets fout gegaan tijdens het opslaan',
                        confirm: 'Oké',
                        cancel: null,
                    });
                }
            );
        } else {
            for (const name of Object.keys(this.form.controls)) {
                this.form.controls[name].markAsTouched();
            }

            this.saving = false;
        }
    }

    setTime(toObject: { begindate?: Date | string | number; enddate?: Date | string | number }) {
        if (toObject.begindate) {
            toObject.begindate = new Date(toObject.begindate);
            Utils.setTime(toObject.begindate, 0, 0);
        }
        if (toObject.enddate) {
            toObject.enddate = new Date(toObject.enddate);
            Utils.setTime(toObject.enddate, 23, 59);
        }
    }
}
